import React, { useMemo } from 'react';
import {
  Checkbox,
  useTheme,
  Box,
  Stack,
  Typography,
  TextField
} from '@mui/material';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { useFiltersByName } from 'src/hooks/useFiltersByName';
import { useUnlimitedScroll } from 'src/hooks/useUnlimitedScroll';
import InfiniteScroll from 'react-infinite-scroll-component';
import { IGroupWithColor } from './EditItemComparison';
import { SketchPicker } from 'react-color';

interface TableProps {
  dataGroup: IGroupWithColor[];
  setDataGroup: React.Dispatch<React.SetStateAction<IGroupWithColor[]>>;
  selectedGroup: IGroupWithColor[];
  setSelectedGroup: React.Dispatch<React.SetStateAction<IGroupWithColor[]>>;
}

const TableGroup: React.FC<TableProps> = ({
  dataGroup,
  setDataGroup,
  selectedGroup,
  setSelectedGroup
}) => {
  const theme = useTheme();

  // Check if all products in a group are selected
  const isGroupSelected = (group: IGroupWithColor) => {
    return selectedGroup.some((item) => item.id === group.id);
  };

  // Handle group checkbox change
  const handleGroupCheckChange = (group: IGroupWithColor, checked: boolean) => {
    if (checked) {
      // Add all products from group
      setSelectedGroup([...selectedGroup, group]);
    } else {
      // Remove all products from group
      setSelectedGroup(selectedGroup.filter((item) => item.id !== group.id));
    }
  };

  const handleChangeColorGroup = (group: IGroupWithColor, color: string) => {
    setDataGroup(
      dataGroup.map((item) => {
        return {
          ...item,
          color: item.id === group.id ? color : item.color
        };
      })
    );
    setSelectedGroup(
      selectedGroup.map((item) => {
        return {
          ...item,
          color: item.id === group.id ? color : item.color
        };
      })
    );
  };

  const handleChangeInputColorGroup = (group: IGroupWithColor) => {
    setDataGroup(
      dataGroup.map((item) => {
        return {
          ...item,
          isInputColor:
            item.id === group.id ? !item.isInputColor : item.isInputColor
        };
      })
    );
    setSelectedGroup(
      selectedGroup.map((item) => {
        return {
          ...item,
          isInputColor:
            item.id === group.id ? !item.isInputColor : item.isInputColor
        };
      })
    );
  };

  // Handle select all checkbox change
  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      // Select all products
      setSelectedGroup(dataGroup);
    } else {
      // Clear all selections
      setSelectedGroup([]);
    }
  };

  const addName = useMemo((): Array<IGroupWithColor> => {
    return dataGroup.map((item) => ({
      ...item,
      name: item.displayName
    }));
  }, [dataGroup]);

  const { filteredList, handleFilter } =
    useFiltersByName<IGroupWithColor>(addName);

  const { fetchMoreData, infiniteScroll } =
    useUnlimitedScroll<IGroupWithColor>(filteredList);

  return (
    <>
      <Box
        sx={{
          border: `1px solid ${theme.palette.grey[300]}`,
          p: theme.spacing(1),
          borderRadius: theme.spacing(1)
        }}
      >
        <Typography fontSize="16px" fontWeight={700}>
          Group
        </Typography>
        <Typography fontSize="14px" color={theme.palette.grey[500]}>
          Select main groups and set chart color.
        </Typography>
      </Box>
      <Box
        sx={{
          border: `1px solid ${theme.palette.grey[300]}`,
          p: theme.spacing(1),
          borderRadius: theme.spacing(1)
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <TextField
            name="keyword"
            variant="outlined"
            size="small"
            autoComplete="off"
            placeholder="Search group"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: 'white',
                fontSize: '14px'
              }
            }}
            onChange={(event) => handleFilter(event.target.value)}
          />
          <Stack direction="row" alignItems="center" justifyContent="center">
            <Checkbox
              checked={dataGroup.length === selectedGroup.length}
              indeterminate={
                selectedGroup.length > 0 &&
                dataGroup.length !== selectedGroup.length
              }
              size="small"
              onChange={handleSelectAllChange}
              indeterminateIcon={
                <IndeterminateCheckBoxIcon
                  sx={{
                    width: theme.spacing(2.5),
                    height: theme.spacing(2.5)
                  }}
                />
              }
            />
            <Typography fontSize="14px" color={theme.palette.grey[500]}>
              Select All Group
            </Typography>
          </Stack>
        </Stack>
        <InfiniteScroll
          dataLength={infiniteScroll.list.length}
          next={fetchMoreData}
          hasMore={infiniteScroll.hasMore}
          loader={<div />}
          height={250}
          endMessage={<div />}
        >
          {infiniteScroll.list.map((group) => (
            <Box
              key={group.id}
              sx={{
                border: `1px solid ${theme.palette.grey[300]}`,
                p: theme.spacing(1),
                borderRadius: theme.spacing(1),
                my: theme.spacing(1)
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={isGroupSelected(group)}
                    onChange={(e) =>
                      handleGroupCheckChange(group, e.target.checked)
                    }
                  />
                  <Box
                    sx={{
                      width: '10px',
                      height: '10px',
                      backgroundColor: group.color,
                      borderRadius: '100%'
                    }}
                  />
                  <Typography fontSize="14px" fontWeight={700}>
                    {group.displayName}
                  </Typography>
                </Stack>
                {/* <Stack>
                  <Box
                    sx={{
                      border: `1px solid ${theme.palette.grey[500]}`,
                      p: theme.spacing(1),
                      borderRadius: theme.spacing(1),
                      cursor: 'pointer'
                    }}
                    onClick={() => handleChangeInputColorGroup(group)}
                  >
                    <Box
                      sx={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: group.color,
                        borderRadius: '100%'
                      }}
                    />
                  </Box>
                  {group.isInputColor ? (
                    <div
                      style={{
                        position: 'absolute',
                        zIndex: '5'
                      }}
                    >
                      <div
                        style={{
                          position: 'fixed',
                          top: '0px',
                          right: '0px',
                          bottom: '0px',
                          left: '0px'
                        }}
                        onClick={() => handleChangeInputColorGroup(group)}
                      />
                      <SketchPicker
                        color={group.color}
                        onChangeComplete={(color) =>
                          handleChangeColorGroup(group, color.hex)
                        }
                      />
                    </div>
                  ) : null}
                </Stack> */}
              </Stack>
            </Box>
          ))}
        </InfiniteScroll>
      </Box>
    </>
  );
};

export default TableGroup;
